import { useRef, useCallback, useState } from "react";
import { createPortal } from "react-dom";
import type { PopoutEntry } from "../hooks/usePopout";
import type { SnapState, WindowSlot } from "../hooks/useWindowManager";
import { Button } from "./ui/button";
import { useI18n } from "../i18n";

const SNAP_THRESHOLD = 20;
const MAGNETIC_THRESHOLD = 24;
const MAGNETIC_GAP = 8;

export interface MagneticEdge {
  orientation: 'vertical' | 'horizontal';
  position: number;
  start: number;
  end: number;
}

interface PopoutWindowProps {
  entry: PopoutEntry;
  title: string;
  onClose: () => void;
  onBringToFront: () => void;
  onMove: (x: number, y: number) => void;
  onResize?: (width: number, height: number) => void;
  hideClose?: boolean;
  children: React.ReactNode;
  /** Snap zone callbacks (optional — provided when WindowManager is active) */
  onSnap?: (snapState: SnapState) => void;
  onUnsnap?: () => void;
  snapState?: SnapState | null;
  /** All windows for magnetic edge detection */
  allWindows?: WindowSlot[];
  /** Callback for snap zone preview overlay */
  onSnapPreview?: (zone: SnapState | null) => void;
  /** Callback for magnetic edge indicator */
  onMagneticEdges?: (edges: MagneticEdge[]) => void;
  /** Icons in the title bar before «—» «×» (the initiative window's «＋» «⋯», brief 25.10 Р1). */
  headerActions?: React.ReactNode;
  /** The window grows with its content up to this height (px), then its body scrolls (Р1: «окно по содержимому»). */
  autoHeightMax?: number;
  /** The «×» tooltip (what closing means, e.g. «the fight goes on»). */
  closeTitle?: string;
  /** No body padding (the content draws its own edges). */
  flushBody?: boolean;
  /** data-testid / data-window of the window (e2e). */
  windowKey?: string;
}

type WindowBounds = Pick<WindowSlot, 'id' | 'x' | 'y' | 'width' | 'height'>;

/** Magnetic alignment uses what is on screen, including minimized and content-sized windows. */
function visibleWindowBounds(windows: WindowSlot[]): WindowBounds[] {
  const allowed = new Set(windows.map(w => w.id));
  return Array.from(document.querySelectorAll<HTMLElement>('[data-popout-id]')).flatMap(el => {
    const id = el.dataset.popoutId!;
    if (!allowed.has(id)) return [];
    const rect = el.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0 ? [{ id, x: rect.x, y: rect.y, width: rect.width, height: rect.height }] : [];
  });
}

function detectSnapZone(clientX: number, clientY: number): SnapState | null {
  const vw = window.innerWidth;
  if (clientX <= SNAP_THRESHOLD) return 'left-half';
  if (clientX >= vw - SNAP_THRESHOLD) return 'right-half';
  if (clientY <= SNAP_THRESHOLD) return 'fullwidth';
  return null;
}

function detectMagneticEdges(
  x: number,
  y: number,
  width: number,
  height: number,
  windows: WindowBounds[],
  currentId: string,
): { edges: MagneticEdge[]; snapX: number | null; snapY: number | null } {
  const edges: MagneticEdge[] = [];
  let snapX: number | null = null;
  let snapY: number | null = null;

  const right = x + width;
  const bottom = y + height;

  for (const win of windows) {
    if (win.id === currentId) continue;

    const wRight = win.x + win.width;
    const wBottom = win.y + win.height;

    // Right edge of current → Left edge of neighbor
    const distRL = Math.abs(right - win.x);
    if (distRL <= MAGNETIC_THRESHOLD) {
      const overlapTop = Math.max(y, win.y);
      const overlapBottom = Math.min(bottom, wBottom);
      if (overlapTop < overlapBottom) {
        snapX = win.x - width - MAGNETIC_GAP;
        edges.push({
          orientation: 'vertical',
          position: win.x - MAGNETIC_GAP / 2,
          start: overlapTop,
          end: overlapBottom,
        });
      }
    }

    // Left edge of current → Right edge of neighbor
    const distLR = Math.abs(x - wRight);
    if (distLR <= MAGNETIC_THRESHOLD) {
      const overlapTop = Math.max(y, win.y);
      const overlapBottom = Math.min(bottom, wBottom);
      if (overlapTop < overlapBottom) {
        snapX = wRight + MAGNETIC_GAP;
        edges.push({
          orientation: 'vertical',
          position: wRight + MAGNETIC_GAP / 2,
          start: overlapTop,
          end: overlapBottom,
        });
      }
    }

    // Bottom edge of current → Top edge of neighbor
    const distBT = Math.abs(bottom - win.y);
    if (distBT <= MAGNETIC_THRESHOLD) {
      const overlapLeft = Math.max(x, win.x);
      const overlapRight = Math.min(right, wRight);
      if (overlapLeft < overlapRight) {
        snapY = win.y - height - MAGNETIC_GAP;
        edges.push({
          orientation: 'horizontal',
          position: win.y - MAGNETIC_GAP / 2,
          start: overlapLeft,
          end: overlapRight,
        });
      }
    }

    // Top edge of current → Bottom edge of neighbor
    const distTB = Math.abs(y - wBottom);
    if (distTB <= MAGNETIC_THRESHOLD) {
      const overlapLeft = Math.max(x, win.x);
      const overlapRight = Math.min(right, wRight);
      if (overlapLeft < overlapRight) {
        snapY = wBottom + MAGNETIC_GAP;
        edges.push({
          orientation: 'horizontal',
          position: wBottom + MAGNETIC_GAP / 2,
          start: overlapLeft,
          end: overlapRight,
        });
      }
    }
  }

  return { edges, snapX, snapY };
}

export default function PopoutWindow({
  entry,
  title,
  onClose,
  onBringToFront,
  onMove,
  onResize,
  hideClose,
  headerActions,
  autoHeightMax,
  closeTitle,
  flushBody,
  windowKey,
  children,
  onSnap,
  onUnsnap,
  snapState,
  allWindows,
  onSnapPreview,
  onMagneticEdges,
}: PopoutWindowProps) {
  const { t } = useI18n();
  const windowRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{
    startX: number;
    startY: number;
    originX: number;
    originY: number;
  } | null>(null);
  const resizeRef = useRef<{
    startX: number;
    startY: number;
    originW: number;
    originH: number;
  } | null>(null);
  const [pendingSnap, setPendingSnap] = useState<SnapState | null>(null);
  const [collapsed, setCollapsed] = useState(false);

  const handlePointerDown = useCallback(
    (e: React.PointerEvent) => {
      e.preventDefault();
      onBringToFront();
      dragRef.current = {
        startX: e.clientX,
        startY: e.clientY,
        originX: entry.x,
        originY: entry.y,
      };
      (e.target as HTMLElement).setPointerCapture(e.pointerId);
    },
    [entry.x, entry.y, onBringToFront],
  );

  const handlePointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (dragRef.current) {
        const dx = e.clientX - dragRef.current.startX;
        const dy = e.clientY - dragRef.current.startY;
        let newX = dragRef.current.originX + dx;
        let newY = dragRef.current.originY + dy;

        // If currently snapped and being dragged, unsnap first
        if (snapState && onUnsnap) {
          onUnsnap();
        }

        // Snap zone detection
        const zone = collapsed ? null : detectSnapZone(e.clientX, e.clientY);
        setPendingSnap(zone);
        onSnapPreview?.(zone);

        // Magnetic edge detection
        if (allWindows && onMagneticEdges) {
          const rect = windowRef.current?.getBoundingClientRect();
          const { edges, snapX, snapY } = detectMagneticEdges(
            newX, newY, rect?.width ?? entry.width, rect?.height ?? entry.height, visibleWindowBounds(allWindows), entry.id,
          );
          onMagneticEdges(edges);
          if (snapX !== null) newX = snapX;
          if (snapY !== null) newY = snapY;
        }

        onMove(newX, newY);
      } else if (resizeRef.current && onResize) {
        const dx = e.clientX - resizeRef.current.startX;
        const dy = e.clientY - resizeRef.current.startY;
        onResize(
          Math.max(200, resizeRef.current.originW + dx),
          Math.max(150, resizeRef.current.originH + dy),
        );
      }
    },
    [onMove, onResize, snapState, onUnsnap, onSnapPreview, allWindows, onMagneticEdges, entry.width, entry.height, entry.id, collapsed],
  );

  const handlePointerUp = useCallback(() => {
    if (!collapsed && dragRef.current && pendingSnap && onSnap) {
      onSnap(pendingSnap);
    }
    dragRef.current = null;
    resizeRef.current = null;
    setPendingSnap(null);
    onSnapPreview?.(null);
    onMagneticEdges?.([]);
  }, [pendingSnap, onSnap, onSnapPreview, onMagneticEdges, collapsed]);

  const cancelPointer = useCallback(() => {
    dragRef.current = null;
    resizeRef.current = null;
    setPendingSnap(null);
    onSnapPreview?.(null);
    onMagneticEdges?.([]);
  }, [onSnapPreview, onMagneticEdges]);

  const handleResizePointerDown = useCallback(
    (e: React.PointerEvent) => {
      e.preventDefault();
      e.stopPropagation();
      onBringToFront();
      resizeRef.current = {
        startX: e.clientX,
        startY: e.clientY,
        originW: entry.width,
        originH: entry.height,
      };
      (e.target as HTMLElement).setPointerCapture(e.pointerId);
    },
    [entry.width, entry.height, onBringToFront],
  );

  const handleDoubleClick = useCallback(
    (e: React.MouseEvent) => {
      // Guard: don't trigger on close button
      if (collapsed || (e.target as HTMLElement).closest('.popout-window__close')) return;
      if (snapState === 'fullwidth' && onUnsnap) {
        onUnsnap();
      } else if (onSnap) {
        onSnap('fullwidth');
      }
    },
    [snapState, onSnap, onUnsnap, collapsed],
  );

  /* opt-out from Dialog migration: custom drag/resize logic incompatible with Radix Dialog (FR-010) */
  return createPortal(
    <div
      ref={windowRef}
      className="popout-window"
      data-popout-id={entry.id}
      style={{
        left: entry.x,
        top: entry.y,
        width: collapsed ? 200 : entry.width,
        height: collapsed || autoHeightMax ? 'auto' : entry.height,
        ...(autoHeightMax && !collapsed ? { maxHeight: `min(${autoHeightMax}px, calc(100vh - ${Math.max(0, entry.y)}px - 8px))` } : {}),
        zIndex: entry.zIndex,
      }}
      data-window={windowKey}
      onMouseDown={onBringToFront}
    >
      <div
        className="popout-window__titlebar"
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={cancelPointer}
        onDoubleClick={handleDoubleClick}
      >
        <span className="popout-window__title">{title}</span>
        <div style={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          {!collapsed && headerActions}
          <Button
            variant="ghost"
            className="popout-window__close"
            onClick={() => setCollapsed(c => !c)}
            onPointerDown={(e) => e.stopPropagation()}
            aria-label={collapsed ? 'Expand' : 'Minimize'}
          >
            {collapsed ? '▢' : '—'}
          </Button>
          {!hideClose && (
            <Button
              variant="ghost"
              className="popout-window__close"
              onClick={onClose}
              onPointerDown={(e) => e.stopPropagation()}
              aria-label={closeTitle ?? t('popout.close')}
              title={closeTitle}
            >
              ×
            </Button>
          )}
        </div>
      </div>
      {!collapsed && (
        <>
          <div className={flushBody ? 'popout-window__body popout-window__body--flush' : 'popout-window__body'}>{children}</div>
          <div
            className="popout-window__resize-handle"
            aria-hidden="true"
            onPointerDown={handleResizePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerCancel={cancelPointer}
          />
        </>
      )}
    </div>,
    document.body,
  );
}
